import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Field, PageHeader, StatusBadge } from "@/components/app/common";
import { AuditMeta, AuditPanel } from "@/components/app/AuditPanel";
import { CustomerFormDialog } from "@/components/app/CustomerFormDialog";
import { ImportDialog } from "@/components/app/ImportDialog";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, downloadFile, errMsg } from "@/lib/api";

function CustomerSheet({ id, onClose, onEdit, refreshKey }) {
  const { data: c } = useFetch(id ? `/customers/${id}?k=${refreshKey}` : null);
  return (
    <Sheet open={!!id} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="customer-detail-sheet">
        {c && <>
          <SheetHeader><SheetTitle className="flex items-center gap-2">{c.name} <StatusBadge status={c.active ? "active" : "inactive"} /></SheetTitle></SheetHeader>
          <div className="mt-2"><AuditMeta record={c} /></div>
          <div className="grid grid-cols-2 gap-4 mt-5">
            <Field k="Code" v={c.code} mono /><Field k="PAN" v={c.pan} mono />
            <Field k="HQ city / state" v={[c.city, c.state].filter(Boolean).join(", ")} />
            <Field k="Region / HQ country" v={[c.region, c.country].filter(Boolean).join(", ")} />
            <Field k="Customer group" v={c.group_name} />
            <Field k="Orders" v={String(c.order_count)} />
            {(c.aliases || []).length > 0 && <div className="col-span-2" data-testid="customer-aliases"><Field k="Also known as" v={c.aliases.join(" · ")} /></div>}
          </div>
          <h3 className="text-sm font-semibold mt-6 mb-2">GST registrations ({(c.registrations || []).length})</h3>
          <div className="space-y-2" data-testid="customer-registrations-list">
            {(c.registrations || []).map((r) => (
              <div key={r.id} className="border border-slate-200 rounded p-2 text-sm">
                <div className="font-mono text-xs">{r.gstin || <span className="text-amber-700">Unregistered / no GSTIN</span>} {r.label && <span className="font-sans text-slate-500">· {r.label}</span>} {(r.city || r.state) && <span className="font-sans text-slate-500">· {[r.city, r.state].filter(Boolean).join(", ")}</span>}</div>
                <div className="text-xs text-slate-600 mt-1">{r.billing_address || "—"}</div>
              </div>
            ))}
            {!(c.registrations || []).length && <p className="text-xs text-amber-700">No GST registration yet — orders will be GST pending.</p>}
          </div>
          <h3 className="text-sm font-semibold mt-6 mb-2">Delivery sites ({(c.sites || []).length})</h3>
          <div className="space-y-2" data-testid="customer-sites-list">
            {(c.sites || []).map((s) => (
              <div key={s.id} className="border border-slate-200 rounded p-2 text-sm">
                <div className="font-medium text-xs">{s.name}</div>
                <div className="text-xs text-slate-600">{[s.address, s.city, s.state, s.pincode].filter(Boolean).join(", ") || "—"}</div>
                {(s.gstin || s.consignee_name || s.contact_person || s.contact_phone) && <div className="text-xs text-slate-500" data-testid={`customer-site-extra-${s.id}`}>{[s.gstin && `GSTIN ${s.gstin}`, s.consignee_name && `Consignee: ${s.consignee_name}`, [s.contact_person, s.contact_phone].filter(Boolean).join(" · ")].filter(Boolean).join(" · ")}</div>}
                {s.map_link && <a href={s.map_link} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-700 hover:underline" data-testid={`customer-site-map-${s.id}`}>Open Google location</a>}
              </div>
            ))}
            {!(c.sites || []).length && <p className="text-xs text-slate-500">No delivery sites.</p>}
          </div>
          <h3 className="text-sm font-semibold mt-6 mb-2">Contacts ({(c.contacts || []).length})</h3>
          <div className="space-y-1 text-xs" data-testid="customer-contacts-list">
            {(c.contacts || []).map((p) => <div key={p.id}><span className="font-medium">{p.name}</span>{p.designation && ` · ${p.designation}`} · {p.phone || "—"} · {p.email || "—"}</div>)}
            {!(c.contacts || []).length && <p className="text-slate-500">No contacts.</p>}
          </div>
          <Button size="sm" variant="outline" className="mt-4" onClick={() => onEdit(c)} data-testid="customer-edit-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Button>
          <h3 className="text-sm font-semibold mt-6 mb-2">History</h3>
          <AuditPanel entityId={c.id} refreshKey={refreshKey} />
        </>}
      </SheetContent>
    </Sheet>
  );
}

export default function Customers() {
  const { can } = useMe();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [active, setActive] = useState("true");
  const [missing, setMissing] = useState("none");
  const [form, setForm] = useState({ open: false, customer: null });
  const [imp, setImp] = useState(false);
  const [hq, setHq] = useState(false);
  const [tick, setTick] = useState(0);
  const openId = params.get("open");
  const { data, reload } = useFetch(`/customers?q=${encodeURIComponent(q)}${active !== "all" ? `&active=${active}` : ""}${missing !== "none" ? `&missing=${missing}` : ""}&limit=1000&t=${tick}`);

  useEffect(() => {
    if (params.get("new") && can("write")) setForm({ open: true, customer: null });
  }, [params, can]);

  const toggle = async (c) => {
    try { await api.post(`/customers/${c.id}/status`, { active: !c.active }); reload(); setTick((t) => t + 1); } catch (e) { toast.error(errMsg(e)); }
  };
  const saved = (c) => { setTick((t) => t + 1); setParams({ open: c.id }); };

  return (
    <div data-testid="customers-page">
      <PageHeader title="Customers Master" subtitle="One governed record per company. GSTIN and company name are checked for duplicates.">
        {can("customers.export") && <Button variant="outline" onClick={() => downloadFile("/customers/export", "Customers_export.xlsx").catch((e) => toast.error(errMsg(e)))} data-testid="customer-export-btn"><Download className="h-4 w-4 mr-1" />Export Excel</Button>}
        {can("write") && <>
          <Button variant="outline" onClick={() => setImp(true)} data-testid="customer-import-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Import Excel</Button>
          <Button variant="outline" onClick={() => setHq(true)} data-testid="customer-excel-update-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Update from Excel</Button>
          <Button onClick={() => setForm({ open: true, customer: null })} data-testid="new-customer-btn"><Plus className="h-4 w-4 mr-1" />New customer</Button>
        </>}
      </PageHeader>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input placeholder="Search name, code, GSTIN, city, region, group…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-sm bg-white" data-testid="customers-search-input" />
        <Select value={active} onValueChange={setActive}>
          <SelectTrigger className="sm:w-40 bg-white" data-testid="customers-active-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="true">Active</SelectItem><SelectItem value="false">Inactive</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
        </Select>
        <Select value={missing} onValueChange={setMissing}>
          <SelectTrigger className="sm:w-56 bg-white" data-testid="customers-missing-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">Any city / region</SelectItem><SelectItem value="any">Missing city or region</SelectItem><SelectItem value="city">Missing city</SelectItem><SelectItem value="region">Missing region</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="th text-left"><th className="px-3 py-2.5">Code</th><th className="px-3 py-2.5">Company</th><th className="px-3 py-2.5">GSTINs</th><th className="px-3 py-2.5">Sites</th><th className="px-3 py-2.5">Group</th><th className="px-3 py-2.5">HQ City</th><th className="px-3 py-2.5">HQ State</th><th className="px-3 py-2.5">Country</th><th className="px-3 py-2.5">Region</th><th className="px-3 py-2.5">Contact</th><th className="px-3 py-2.5">Updated by</th><th className="px-3 py-2.5">Active</th></tr></thead>
          <tbody>
            {(data || []).map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setParams({ open: c.id })} data-testid={`customer-row-${c.code}`}>
                <td className="px-3 py-2.5 font-mono text-xs">{c.code}</td>
                <td className="px-3 py-2.5 font-medium">{c.name}{c.aliases?.length > 0 && <div className="text-[11px] text-slate-500 font-normal">aka {c.aliases.join(", ")}</div>}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{c.gstins?.length ? <>{c.gstins[0]}{c.gstins.length > 1 && <span className="ml-1 text-sky-700" data-testid={`customer-gstin-count-${c.code}`}>+{c.gstins.length - 1}</span>}</> : <span className="text-amber-700">pending</span>}</td>
                <td className="px-3 py-2.5 text-xs">{(c.sites || []).length}</td>
                <td className="px-3 py-2.5 text-xs">{c.group_name || "—"}</td>
                <td className="px-3 py-2.5 text-xs">{c.city || "—"}</td>
                <td className="px-3 py-2.5 text-xs">{c.state || "—"}</td>
                <td className="px-3 py-2.5 text-xs">{c.country || "—"}</td>
                <td className="px-3 py-2.5 text-xs">{c.region || "—"}</td>
                <td className="px-3 py-2.5 text-xs">{c.contact_name || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{c.updated_by?.name}</td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <Switch checked={c.active} disabled={!can("write")} onCheckedChange={() => toggle(c)} data-testid={`customer-active-toggle-${c.code}`} />
                </td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={12} className="px-3 py-10 text-center text-slate-500">No customers found</td></tr>}
          </tbody>
        </table>
      </div>
      <CustomerSheet id={openId} refreshKey={tick} onClose={() => setParams({})} onEdit={(c) => setForm({ open: true, customer: c })} />
      <CustomerFormDialog open={form.open} customer={form.customer} onSaved={saved}
        onOpenChange={(v) => { setForm({ ...form, open: v }); if (!v && params.get("new")) setParams({}); }} />
      <ImportDialog entity="customers" open={imp} onOpenChange={setImp} onDone={() => setTick((t) => t + 1)} />
      <ImportDialog entity="customers" path="/customers/bulk-update" title="Update existing customers from Excel" commitLabel="Apply updates"
        templatePath="/customers/export" templateName="Customers_export.xlsx" templateLabel="Download current customers"
        hint="Export, edit group / HQ city, state, country / region / contact / phone / email / active, then upload. Rows match on customer_id; blank cells keep the current value. Name, GSTIN and sites are not changed here. Preview first, then apply."
        open={hq} onOpenChange={setHq} onDone={() => setTick((t) => t + 1)} />
    </div>
  );
}
